'use client';

import {
  useState,
  useEffect,
  FC,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import { TAX_YEARS, CURRENT_TAX_YEAR } from '@/_constants/year-options';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UploadSignatureRequestDialogProps } from './UploadSignatureRequestDialog.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { SIGNATURE_REQUEST_PATH } from '@/_constants/signature-request';
import { UserContext } from '@/_contexts/UserProvider';
import { uploadSignatureRequest } from '@/_api/admin-signature-requests';
import {
  Flex,
  File,
  Dialog,
  UploadBox,
  Select,
  Button,
  ButtonGroup,
  EntitySelectorInputChips,
  Separator,
} from '@/_components';
import { SignatureRequestsContext } from '../../SignatureRequestsProvider';

export const UploadSignatureRequestDialog: FC<
  UploadSignatureRequestDialogProps
> = ({ onUploaded }) => {
  const { token } = useContext(UserContext);

  const { refreshCompanySignatureRequests, selectedCompany } =
    useContext(CompanyContext);

  const { addToast, resetToasts } = useContext(ToastContext);
  const { isSending, setSending } = useContext(SignatureRequestsContext);

  const onClose = useCallback(() => setSending(false), [setSending]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [taxYear, setTaxYear] = useState<string>(CURRENT_TAX_YEAR);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Array<number>>(
    [],
  );

  useEffect(() => {
    if (!isSending) {
      resetToasts();
      setSelectedFile(null);
      setLoading(false);
    }
  }, [isSending, resetToasts]);

  // Preselect the selected company
  useEffect(() => {
    if (selectedCompany?.id) {
      onChipsChange([selectedCompany?.id]);
    }
  }, [selectedCompany]);

  const onDrop = useCallback(async (acceptedFiles: Array<File>) => {
    setSelectedFile(acceptedFiles[0]);
  }, []);

  const onYearChange = useCallback(
    (event: any) => setTaxYear(event.target.value),
    [],
  );

  const onUpload = useCallback(async () => {
    if (!selectedCompanyIds || !selectedFile || selectedFile?.size === 0) {
      return;
    }

    try {
      setLoading(true);

      const path = `${SIGNATURE_REQUEST_PATH}/${taxYear}`;

      await uploadSignatureRequest({
        companyId: selectedCompanyIds?.[0],
        file: selectedFile,
        path,
        documentName: selectedFile.name,
        taxYear: parseInt(taxYear),
        token,
      });

      refreshCompanySignatureRequests();
      onClose();

      if (onUploaded) {
        onUploaded(selectedFile, path);
      }
    } catch (error) {
      return addToast('error', 'File upload failed, please try again.', {
        error,
      });
    } finally {
      setLoading(false);
    }
  }, [
    selectedFile,
    selectedCompanyIds,
    taxYear,
    token,
    onClose,
    addToast,
    onUploaded,
    refreshCompanySignatureRequests,
  ]);

  const onChipsChange = (chips: Array<number>) => setSelectedCompanyIds(chips);

  const isUploadable: boolean = useMemo(
    () => selectedCompanyIds?.length === 1 && !!selectedFile && !loading,
    [selectedCompanyIds, selectedFile, loading],
  );

  return (
    <Dialog
      isOpen={isSending}
      isNarrow={true}
      title="Upload Signature Request"
      onClose={onClose}
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          <EntitySelectorInputChips
            label="Select Entity"
            preselectedEntityIds={
              selectedCompany?.id ? [selectedCompany?.id] : []
            }
            onChipsChange={onChipsChange}
            isSingleSelection={true}
          />
          <UploadBox
            onDrop={onDrop}
            isLoading={loading}
            accept="application/pdf"
          />

          {selectedFile && (
            <File title={selectedFile.name}>
              <Button
                isLoading={loading}
                intent="danger-secondary"
                value="Remove"
                size="tiny"
                isIconOnly={true}
                iconRight="trash"
                onClick={() => setSelectedFile(null)}
              />
            </File>
          )}

          <Select
            label="Tax year"
            items={TAX_YEARS}
            selectedValue={taxYear}
            onChange={onYearChange}
          />
          <Separator isLight={true} />
          <ButtonGroup align="right">
            <Button
              value="Cancel"
              onClick={onClose}
              intent="danger-secondary"
            />
            <Button
              isDisabled={!isUploadable}
              value="Upload"
              onClick={onUpload}
              intent="primary"
              isLoading={loading}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
